import { useState } from "react";
import { ShieldCheck, ShieldX, Search, Loader2 } from "lucide-react";
import PublicShell from "@/components/public/PublicShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";

export default function Verify() {
    const [id, setId] = useState("");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    const verify = async (e) => {
        e.preventDefault();
        if (!id.trim()) return;
        setLoading(true); setResult(null);
        try {
            const { data } = await api.get(`/verify/${encodeURIComponent(id.trim())}`);
            setResult(data);
        } catch {
            setResult({ valid: false, message: "Something went wrong. Please try again." });
        } finally { setLoading(false); }
    };

    return (
        <PublicShell>
            <section className="section-pad grid-bg">
                <div className="container-page max-w-2xl">
                    <p className="overline">Credential Verification</p>
                    <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">Verify a LARUTA credential</h1>
                    <p className="mt-4 text-lg text-foreground/70">Enter a credential ID to confirm its authenticity. Try <span className="font-mono text-sm bg-muted px-2 py-0.5 rounded">LARUTA-AIP-L2-2026-000127</span></p>
                    <form onSubmit={verify} className="mt-8 flex gap-2">
                        <Input data-testid="verify-input" value={id} onChange={(e) => setId(e.target.value)} placeholder="LARUTA-AIP-L4-2026-000127" className="h-12 font-mono" />
                        <Button type="submit" size="lg" disabled={loading} className="rounded-full h-12 px-6" data-testid="verify-submit-btn">
                            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Search className="mr-2 h-4 w-4" /> Verify</>}
                        </Button>
                    </form>

                    {result && (
                        <Card className={`mt-8 p-6 border-2 ${result.valid ? "border-success/40" : "border-destructive/40"}`} data-testid="verify-result">
                            {result.valid ? (
                                <>
                                    <div className="flex items-center gap-3">
                                        <span className="grid h-12 w-12 place-items-center rounded-full bg-success/15 text-success"><ShieldCheck className="h-6 w-6" /></span>
                                        <div>
                                            <p className="font-heading text-lg font-bold">Verified Credential</p>
                                            <Badge className="bg-success/15 text-success border-0">{result.status}</Badge>
                                        </div>
                                    </div>
                                    <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                                        {[["Holder", result.holder_name], ["Program / Skill", result.program], ["Type", result.credential_type], ["Level", result.level], ["Issued", result.issue_date], ["Credential ID", result.credential_id]].map(([k, v]) => (
                                            <div key={k}><dt className="text-foreground/50">{k}</dt><dd className="mt-0.5 font-medium font-mono text-xs sm:text-sm break-all">{v}</dd></div>
                                        ))}
                                    </dl>
                                </>
                            ) : (
                                <div className="flex items-center gap-3">
                                    <span className="grid h-12 w-12 place-items-center rounded-full bg-destructive/15 text-destructive"><ShieldX className="h-6 w-6" /></span>
                                    <div>
                                        <p className="font-heading text-lg font-bold">Not Verified</p>
                                        <p className="text-sm text-foreground/60">{result.message}</p>
                                    </div>
                                </div>
                            )}
                        </Card>
                    )}
                </div>
            </section>
        </PublicShell>
    );
}
